/**
 * GameHistory UI Component
 * Displays a list of past games with filtering and pagination
 */

import { gameHistoryService, type GameWithPlayers, type GameType } from '../../history';
import { authService } from '../../auth';

export interface GameHistoryOptions {
  container: HTMLElement;
  pageSize?: number;
  onGameSelect?: (gameId: string) => void;
}

export class GameHistory {
  private container: HTMLElement;
  private pageSize: number;
  private currentOffset: number = 0;
  private totalGames: number = 0;
  private currentFilter: GameType | 'all' = 'all';
  private games: GameWithPlayers[] = [];
  private currentUserId: string | null = null;
  private onGameSelect?: (gameId: string) => void;
  private isLoading: boolean = false;

  constructor(options: GameHistoryOptions) {
    this.container = options.container;
    this.pageSize = options.pageSize || 10;
    this.onGameSelect = options.onGameSelect;

    const user = authService.getCurrentUser();
    this.currentUserId = user?.id || null;

    this.render();
    this.loadGames();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="game-history">
        <div class="game-history-header">
          <h2 class="game-history-title">Game History</h2>
          <div class="game-history-filters">
            <select class="game-history-filter-select">
              <option value="all">All Games</option>
              <option value="online">Online</option>
              <option value="vs_ai">vs AI</option>
              <option value="local">Local</option>
            </select>
          </div>
        </div>

        <div class="game-history-list">
          <div class="game-history-loading">Loading games...</div>
        </div>

        <div class="game-history-footer">
          <button class="game-history-load-more" style="display: none;">
            Load More
          </button>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    // Filter select
    const filterSelect = this.container.querySelector('.game-history-filter-select') as HTMLSelectElement;
    filterSelect?.addEventListener('change', () => {
      this.currentFilter = filterSelect.value as GameType | 'all';
      this.currentOffset = 0;
      this.games = [];
      this.loadGames();
    });

    // Load more button
    const loadMoreBtn = this.container.querySelector('.game-history-load-more') as HTMLButtonElement;
    loadMoreBtn?.addEventListener('click', () => {
      this.loadMoreGames();
    });
  }

  private async loadGames(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    const listContainer = this.container.querySelector('.game-history-list') as HTMLElement;
    listContainer.innerHTML = '<div class="game-history-loading">Loading games...</div>';

    const filter = this.currentFilter !== 'all' ? { gameType: this.currentFilter as GameType } : undefined;
    const { games, total } = await gameHistoryService.getMyGames(this.pageSize, this.currentOffset, filter);

    this.games = games;
    this.totalGames = total;
    this.isLoading = false;

    this.renderGameList();
    this.updateLoadMoreButton();
  }

  private async loadMoreGames(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    this.currentOffset += this.pageSize;
    const filter = this.currentFilter !== 'all' ? { gameType: this.currentFilter as GameType } : undefined;
    const { games } = await gameHistoryService.getMyGames(this.pageSize, this.currentOffset, filter);

    this.games = [...this.games, ...games];
    this.isLoading = false;

    this.renderGameList();
    this.updateLoadMoreButton();
  }

  private renderGameList(): void {
    const listContainer = this.container.querySelector('.game-history-list') as HTMLElement;

    if (this.games.length === 0) {
      listContainer.innerHTML = `
        <div class="game-history-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p>No games found</p>
          <span>Play some games to see your history here!</span>
        </div>
      `;
      return;
    }

    listContainer.innerHTML = this.games.map(game => this.renderGameCard(game)).join('');

    // Attach click handlers
    const cards = listContainer.querySelectorAll('.game-history-card');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        const gameId = card.getAttribute('data-game-id');
        if (gameId && this.onGameSelect) {
          this.onGameSelect(gameId);
        }
      });
    });
  }

  private renderGameCard(game: GameWithPlayers): string {
    const result = this.currentUserId ? gameHistoryService.getResultForPlayer(game, this.currentUserId) : null;
    const opponent = this.getOpponent(game);
    const resultClass = result === 'win' ? 'result-win' : result === 'loss' ? 'result-loss' : 'result-draw';
    const resultText = result === 'win' ? 'W' : result === 'loss' ? 'L' : 'D';
    
    const avatarHtml = opponent?.avatarUrl
      ? `<img class="game-history-avatar" src="${opponent.avatarUrl}" alt="">`
      : `<div class="game-history-avatar-placeholder">${this.getInitial(opponent)}</div>`;

    const opponentName = this.getOpponentName(game, opponent);
    const dateStr = this.formatDate(game.endedAt || game.startedAt);
    const gameTypeBadge = this.getGameTypeBadge(game.gameType);
    const moveCount = game.moveHistory?.length || 0;

    return `
      <div class="game-history-card" data-game-id="${game.id}">
        <div class="game-history-result ${resultClass}">
          <span class="result-badge">${resultText}</span>
        </div>
        <div class="game-history-opponent">
          ${avatarHtml}
          <div class="game-history-opponent-info">
            <span class="game-history-opponent-name">${this.escapeHtml(opponentName)}</span>
            <span class="game-history-meta">${moveCount} moves</span>
          </div>
        </div>
        <div class="game-history-details">
          <span class="game-history-date">${dateStr}</span>
          <span class="game-history-board">${game.boardSize}x${game.boardSize}</span>
          ${gameTypeBadge}
        </div>
        <div class="game-history-action">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </div>
    `;
  }

  private getOpponent(game: GameWithPlayers): { id: string; username: string; displayName: string | null; avatarUrl: string | null } | null {
    if (!this.currentUserId) return game.player2 || game.player1;
    
    if (game.player1Id === this.currentUserId) {
      return game.player2;
    }
    return game.player1;
  }

  private getOpponentName(game: GameWithPlayers, opponent: { displayName: string | null; username: string } | null): string {
    if (game.gameType === 'vs_ai') {
      return 'AI Opponent';
    }
    if (game.gameType === 'local') {
      return 'Local Player';
    }
    if (!opponent) {
      return 'Unknown Player';
    }
    return opponent.displayName || opponent.username;
  }

  private getInitial(player: { username: string } | null): string {
    if (!player) return '?';
    return (player.username[0] || '?').toUpperCase();
  }

  private getGameTypeBadge(gameType: string): string {
    switch (gameType) {
      case 'online':
        return '<span class="game-type-badge badge-online">Online</span>';
      case 'vs_ai':
        return '<span class="game-type-badge badge-ai">AI</span>';
      case 'local':
        return '<span class="game-type-badge badge-local">Local</span>';
      default:
        return '';
    }
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  private updateLoadMoreButton(): void {
    const loadMoreBtn = this.container.querySelector('.game-history-load-more') as HTMLButtonElement;
    const hasMore = this.games.length < this.totalGames;
    loadMoreBtn.style.display = hasMore ? 'block' : 'none';
    loadMoreBtn.textContent = this.isLoading ? 'Loading...' : 'Load More';
    loadMoreBtn.disabled = this.isLoading;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Refresh the game history
   */
  refresh(): void {
    this.currentOffset = 0;
    this.games = [];
    this.loadGames();
  }

  /**
   * Update when user logs in/out
   */
  updateCurrentUser(): void {
    const user = authService.getCurrentUser();
    this.currentUserId = user?.id || null;
    this.refresh();
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}
